import "../../Common/css/user.css";
import "../../Common/css/styles.css";
import { createNotice, deleteNotice, getNotices } from "src/Services/notice_api";
import React from "react";
import { Button, Dialog, Divider, FormLabel, Grid, IconButton, Menu, MenuItem, TextField } from "@mui/material";
import { MoreVert } from "@mui/icons-material";
import TableToolbar from "../TableToolbar";
import { PlusOutlined } from "@ant-design/icons";
import Dialogbox from "../Dialogbox";
import Spinner from "../Spinner";
import CustomAlert from "../Alerts";

export default function AdminNoticesComponent() {
    const [title, setTitle] = React.useState<string>("");
    const [description, setDescription] = React.useState<string>("");
    const [isLoading, setIsLoading] = React.useState<boolean>(false);
    const [alertOpen, setAlertOpen] = React.useState<boolean>(false);
    const [alertDescription, setAlertDescription] = React.useState<string>("");
    const [alertType, setAlertType] = React.useState<"success" | "error" | "warning">("success");
    const [notices, setNotices] = React.useState<any[]>([]);
    const [searchTerm, setSearchTerm] = React.useState<string>("");
    const [openModal, setOpenModal] = React.useState<boolean>(false);
    const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
    const [selectedNotice, setSelectedNotice] = React.useState<any>(null);
    const [viewDialogOpen, setViewDialogOpen] = React.useState<boolean>(false);
    const [isOpen, setIsOpen] = React.useState<boolean>(false);

    const showAlert = (description: string, type: "success" | "error" | "warning") => {
        setAlertDescription(description);
        setAlertType(type);
        setAlertOpen(true);
    };

    const userId = sessionStorage.getItem("adminId") || "";
    const token = sessionStorage.getItem("token") || "";

    const ADMIN = 3;

    const handleAddNotice = async () => {
        setIsLoading(true);

        const body = {
            userId: userId,
            userType: ADMIN,
            noticeTitle: title,
            notice: description
        };

        try {
            await createNotice(body, token);
            setIsLoading(false);
            showAlert("Notice added successfully!", "success");
            setTitle("");
            setDescription("");
            handleGetNotices();
        } catch (error) {
            setIsLoading(false);
            showAlert("Failed to add notice.", "error");
        }
    }

    const handleGetNotices = async () => {
        try {
            const res = await getNotices(token);
            const uid = Number(userId);

            const filtered = res.filter((n: any) =>
                n.userType === 3 && n.userId === uid
            );

            setNotices(filtered);
        } catch (error) {
            setNotices([]);
        }
    }

    React.useEffect(() => {
        handleGetNotices();
    }, []);

    const filteredNotices = notices.filter((notice) => {
        const term = searchTerm.toLowerCase();

        return (
            notice.noticeTitle?.toLowerCase().includes(term) ||
            notice.noticeMessage?.toLowerCase().includes(term)
        );
    });


    const handleMenuClick = (event: React.MouseEvent<HTMLElement>, notice: any) => {
        setAnchorEl(event.currentTarget);
        setSelectedNotice(notice);
    }

    const handleMenuClose = () => {
        setAnchorEl(null);
        setSelectedNotice(null);
    }

    const handleDelete = async (noticeId: number) => {
        try {
            await deleteNotice(noticeId.toString(), userId, ADMIN.toString(), token);
            showAlert("Notice deleted successfully!", "success");
            handleGetNotices();
            setIsOpen(false);
            handleMenuClose();
        } catch (error) {
            showAlert("Failed to delete notice.", "error");
        }
    }

    return (
        <div className="comman-outer">
            <CustomAlert
                open={alertOpen}
                onClose={() => setAlertOpen(false)}
                description={alertDescription}
                type={alertType}
                timeout={3000}
            />
            <Spinner isLoading={isLoading} />

            <Dialogbox
                open={isOpen}
                title="Delete Notice"
                content="Are you sure you want to delete this notice?"
                agreeButtonText="Delete"
                disagreeButtonText="Cancel"
                onAgree={() => handleDelete(selectedNotice.id)}
                onDisagree={() => setIsOpen(false)}
                onClose={() => setIsOpen(false)}
            />

            <div>
                <div className="table-title-outer">
                    <div className="table-title">Add New Notice</div>
                </div>
                <div className="space-bar"></div>
                <div className="card-outer">
                    <Grid container spacing={2} style={{ padding: "16px" }} className="form-grid ">
                        <Grid item xs={12}>
                            <FormLabel component="legend" sx={{ color: "#3d3d3dff", mb: 1 }}>Notice Title</FormLabel>
                            <TextField
                                fullWidth
                                variant="outlined"
                                placeholder="Notice Title"
                                size="small"
                                value={title}
                                onChange={(e) => setTitle(e.target.value)}
                                sx={{
                                    backgroundColor: "black",
                                    input: {
                                        color: "#ffffff",
                                        "::placeholder": {
                                            color: "grey",
                                            opacity: 1,
                                        },
                                    },
                                    "& .MuiOutlinedInput-root": {
                                        "& fieldset": {
                                            borderColor: "#555",
                                        },
                                        "&:hover fieldset": {
                                            borderColor: "#aaa",
                                        },
                                        "&.Mui-focused fieldset": {
                                            borderColor: "gray",
                                        },
                                    },
                                }}
                            />
                        </Grid>

                        <Grid item xs={12}>
                            <FormLabel component="legend" sx={{ color: "#3d3d3dff", mb: 1 }}>Notice Description</FormLabel>
                            <TextField
                                fullWidth
                                variant="outlined"
                                placeholder="Notice Description"
                                size="small"
                                multiline
                                rows={4}
                                value={description}
                                onChange={(e) => setDescription(e.target.value)}
                                sx={{
                                    backgroundColor: "black",
                                    "& textarea": {
                                        color: "#ffffff",                // text color
                                    },
                                    "& textarea::placeholder": {
                                        color: "grey",                   // placeholder
                                        opacity: 1,
                                    },
                                    "& .MuiOutlinedInput-root": {
                                        "& fieldset": {
                                            borderColor: "#555",
                                        },
                                        "&:hover fieldset": {
                                            borderColor: "#aaa",
                                        },
                                        "&.Mui-focused fieldset": {
                                            borderColor: "gray",
                                        },
                                    },
                                }}
                            />
                        </Grid>
                    </Grid>
                </div>
                <div className="pagination-footer">
                    <div></div>

                    <Button
                        className="primary-btn"
                        variant="contained"
                        size="small"
                        onClick={handleAddNotice}
                        startIcon={<PlusOutlined style={{ fontSize: "14px", color: "black" }} />}
                        disabled={!title || !description}
                    >
                        Add Notice
                    </Button>
                </div>
            </div>

            <Divider style={{ margin: "24px 0", borderColor: "#444" }} />

            <div>
                <TableToolbar
                    title="Notice History"
                    searchTerm={searchTerm}
                    setSearchTerm={setSearchTerm}
                    isButton={false}
                    buttonText="Notice"
                    onButtonClick={() => setOpenModal(true)}
                    buttonIcon={<PlusOutlined style={{ fontSize: "14px", color: "black" }} />}
                />
                <div className="space-bar"></div>

                {filteredNotices.length > 0 ? filteredNotices.map((notice) => (
                    <div key={notice.id} className="card-outer" style={{ marginBottom: "16px" }}>
                        <div className="card-outer1">
                            <p className="t-card">Title: {notice.noticeTitle}</p>
                            <p className="t-card">Description: {notice.noticeMessage.length > 20 ? `${notice.noticeMessage.substring(0, 20)}...` : notice.noticeMessage}</p>
                            <p className="t-card">Date: {notice.createdAt.split("T")[0]}</p>
                            <p className="t-card">Time: {notice.createdAt.split("T")[1].split(".")[0]}</p>
                        </div>
                        <IconButton onClick={(e) => handleMenuClick(e, notice)}>
                            <MoreVert style={{ color: "white" }} />
                        </IconButton>
                    </div>
                )) : (
                    <div className="no-results" style={{ padding: "16px", textAlign: "center" }}>
                        <p>No notices found.</p>
                    </div>
                )}

                <div className="pagination-footer"></div>

                <Menu
                    anchorEl={anchorEl}
                    open={Boolean(anchorEl)}
                    onClose={handleMenuClose}
                    anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                    transformOrigin={{ vertical: "top", horizontal: "right" }}
                >
                    <MenuItem onClick={() => setViewDialogOpen(true)}>View</MenuItem>
                    <MenuItem onClick={() => setIsOpen(true)}>Delete</MenuItem>
                </Menu>

                <Dialog
                    open={viewDialogOpen}
                    onClose={() => {
                        setViewDialogOpen(false)
                        handleMenuClose();
                    }}
                    fullWidth
                    maxWidth="sm"
                    PaperProps={{
                        sx: { backgroundColor: "#c6c2c2e3", color: "white", paddingBottom: 2, borderRadius: 2, boxShadow: 24, border: "1px solid #ffffffff" },
                    }}
                >
                    <div style={{ display: "flex", justifyContent: "center", alignItems: "center", borderBottom: "1px solid #555", backgroundColor: "#393939", padding: "25px 16px" }}>
                        <h3>Notice Details</h3>
                    </div>

                    <Grid container spacing={2} style={{ padding: "25px 16px", color: "white" }} className="form-grid">
                        <Grid item xs={12}>
                            <strong>Name:</strong> {selectedNotice?.noticeTitle}
                        </Grid>
                        <Grid item xs={12}>
                            <strong>Description:</strong> {selectedNotice?.noticeMessage}
                        </Grid>
                        <Grid item xs={12}>
                            <strong>Date:</strong> {selectedNotice?.createdAt.split("T")[0]}
                        </Grid>
                        <Grid item xs={12}>
                            <strong>Time:</strong> {selectedNotice?.createdAt.split("T")[1].split(".")[0]}
                        </Grid>
                    </Grid>
                </Dialog>

            </div>
        </div>
    )
}